<template>
    <div v-if="isLoading">
        <font-awesome-icon icon="spinner" spin/>
    </div>
    <div v-else class="settings-app">
        <OmniPageTitle :title="$t('omt.gui.menu.label.settings')" icon="pe-7s-flag" />

        <OmniCard :key="currentGroupIdentifier">
            <template #default>
                <BRow>
                    <BCol cols="12" lg="2">
                        <omni-sidebar :items="groups"/>
                    </BCol>

                    <BCol cols="12" lg="10" class="parameters px-0">
                        <div class="inner-container">
                            <b-row></b-row>

                            <b-row>
                                <b-col cols="12" lg="10">
                                    <OmniAlertInline class="mb-4" variant="warning" size="sm">
                                        Let op: Door onderstaande instellingen te wijzigen, wijzigt u dit voor alle gebruikers.
                                    </OmniAlertInline>
                                </b-col>
                                <b-col cols="12" lg="2" class="text-right">
                                    <b-button @click="save" :disabled="!parametersChanged.length || isSaving"
                                              :variant="parametersChanged.length ? 'success' : 'light'"
                                             :is-processing="isSaving"
                                             icon="save"
                                             size="lg"
                                    >
                                        <span v-if="!isSaving">Opslaan</span>
                                        <font-awesome-icon v-else icon="spinner" spin/>
                                    </b-button>
                                </b-col>
                            </b-row>

                            <b-row v-for="parameter in currentGroupParameters"
                                :data-parameter-group="currentGroupIdentifier"
                                :data-parameter-key="parameter.key"
                                :key="`group-${currentGroupIdentifier}-param-${parameter.key}`"
                                class="mt-4"
                            >
                                <b-col>
                                    <OmniCard class="mb-3 px-1 px-lg-2" :class="{'border-warning': isParameterChanged(parameter)}">
                                        <template #header>
                                            <b-card-title class="mb-0">{{ $t(`frontend-parameters.parameters.${currentGroupIdentifier}.${parameter.key}.title`) | uppercase }}</b-card-title>
                                        </template>

                                        <template #default>
                                            <p v-if="$t(`frontend-parameters.parameters.${currentGroupIdentifier}.${parameter.key}.description`)">
                                                <i v-html="$t(`frontend-parameters.parameters.${currentGroupIdentifier}.${parameter.key}.description`)"/>
                                            </p>

                                            <!-- NORMAL PARAMETERS -->
                                            <div v-if="!parameter.items">
                                                <parameter-input v-model="values[parameter.key]" :parameter="parameter" :parameter-group-identifier="currentGroupIdentifier"/>
                                            </div>

                                            <!-- GROUPS -->
                                            <div v-else>
                                                <parameter-generic-sub-group v-if="parameter.groupType === 'generic'"
                                                                             v-model="values[parameter.key]"
                                                                             :parameters="parameter.items"
                                                                             :parameter-group-identifier="currentGroupIdentifier"
                                                                             :sub-group-identifier="parameter.key"
                                                />
                                            </div>
                                        </template>
                                    </OmniCard>
                                </b-col>
                            </b-row>

                            <div class="text-right mt-3">
                                <b-button @click="save" :disabled="!parametersChanged.length || isSaving"
                                          :variant="parametersChanged.length ? 'success' : 'light'"
                                          :is-processing="isSaving"
                                          icon="save"
                                          size="lg"
                                >
                                    <span v-if="!isSaving">Opslaan</span>
                                    <font-awesome-icon v-else icon="spinner" spin/>
                                </b-button>
                            </div>
                        </div>
                    </BCol>
                </BRow>
            </template>
        </OmniCard>
    </div>
</template>

<style lang="scss">
.settings-app {
    .omni-card {
        padding: 1rem;
        .omni-card-body:first-child {
            padding: 1rem;
        }
    }
}
</style>

<script>
import OmniCard      from "@/components/OmniStock/OmniCard.vue";
import OmniPageTitle from "@/components/OmniStock/OmniPageTitle.vue";
import OmniSidebar   from "@/components/OmniStock/OmniSidebar.vue";
import _ from "lodash"
import LabelValuePairsInput from "@/components/ParameterCustomInputs/LabelValuePairs.vue";
import ParameterGenericSubGroup from "@/components/ParameterSubGroups/GenericSubGroup.vue";
import ParameterInput from "@/components/ParameterInput.vue";

export default {
    name: "SettingsIndex",

    components: {
        ParameterInput,
        ParameterGenericSubGroup,
        LabelValuePairsInput,
        OmniSidebar,
        OmniCard,
        OmniPageTitle,
    },

    data() {
        return {
            isLoading: true,
            isSaving: false,
            parameterGroups: [],
            values: {},
            originalValues: {},
            parametersChanged: [],
            debounceSave: _.debounce(this.save, 1500),
            currentGroupIdentifier: null,
        };
    },

    mounted() {
        this.currentGroupIdentifier = this.$route.params.groupIdentifier

        this.$http.get('/api/frontend/parameters')
            .then(response => {
                this.parameterGroups = response.data
                this.switchGroup()

                if (!this.currentGroupIdentifier) {
                    this.$router.push({
                        name: 'settings',
                        params: {groupIdentifier: this.parameterGroups[0].identifier}
                    })
                }

                this.isLoading = false
            })
    },

    computed: {
        groups() {
            const tree = {};

            this.parameterGroups.forEach(group => {
                if (group.parentIdentifier) {
                    if (!tree[group.parentIdentifier]) {
                        tree[group.parentIdentifier] = {
                            title: this.$t(`frontend-parameters.groups.${group.parentIdentifier}`),
                            children: [],
                        }
                    }

                    tree[group.parentIdentifier].children.push({
                        title: this.$t(`frontend-parameters.groups.${group.identifier}`),
                        to: {
                            name: 'settings',
                            params: {
                                groupIdentifier: group.identifier,
                            },
                        }
                    })
                }
            })

            return tree
        },
        currentGroup() {
            return this.parameterGroups.find(g => g.identifier === this.currentGroupIdentifier) ?? {}
        },
        currentGroupParameters() {
            return this.currentGroup?.parameters ?? []
        },
    },

    methods: {
        isParameterChanged(parameter) {
            return this.parametersChanged.includes(parameter.key)
        },
        switchGroup() {
            this.values = structuredClone(this.$store.getters.getFrontendParameterValues(this.currentGroupIdentifier))
            this.originalValues = structuredClone(this.values)
        },
        async save() {
            if (!this.parametersChanged.length) {
                return;
            }

            this.isSaving = true
            let saveValues = {}
            this.parametersChanged.forEach(p => {
                saveValues[p] = this.values[p]
            })

            return this.$root.setFrontendParameterValues(this.currentGroupIdentifier, saveValues, false)
                .then(() => {
                    this.parametersChanged = []
                    this.originalValues = structuredClone(this.values)
                })
                .finally(() => this.isSaving = false)
        },
    },

    watch: {
        currentGroupIdentifier(val) {
            this.switchGroup()
        },
        values: {
            deep: true,
            handler(newVal) {
                this.parametersChanged = []
                _.forEach(newVal, (value, key) => {
                    if (!_.isEqual(this.originalValues[key], value)) {
                        this.parametersChanged.push(key)
                        this.debounceSave()
                    }
                })
            },
        },
        '$route.params.groupIdentifier': {
            handler(newVal) {
                this.currentGroupIdentifier = newVal
            },
        },
    },
};
</script>

<style lang="scss" scoped>
.settings-app {
    .parameters {
        font-size: 90%;
    }
}
</style>
